"use client";

import Link from "next/link";
import { BellRing, CalendarDays, Clock3, Search, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

type CustomerNotification = {
  publicId: string;
  partySize: number;
  requestedDates: string[];
  timeFrom: string;
  timeUntil: string;
  status: "active" | "fulfilled" | "cancelled" | "expired";
  expiresAt: string;
  createdAt: string;
};

const tokenPrefix = "tables:notify:";

export function CustomerNotifications() {
  const [requests, setRequests] = useState<CustomerNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const tokens = Object.keys(window.localStorage)
      .filter((key) => key.startsWith(tokenPrefix))
      .map((key) => window.localStorage.getItem(key))
      .filter((value): value is string => Boolean(value));

    if (!tokens.length) {
      window.queueMicrotask(() => setLoading(false));
      return;
    }

    Promise.allSettled(
      tokens.map(async (token) => {
        const response = await fetch(
          `/api/tables/notify-requests/${encodeURIComponent(token)}`,
          { cache: "no-store" },
        );
        if (!response.ok) throw new Error("Notification unavailable");
        const payload = (await response.json()) as
          | CustomerNotification
          | { data: CustomerNotification };
        return "data" in payload ? payload.data : payload;
      }),
    ).then((results) => {
      const loaded = results.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      );
      setRequests(loaded.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
      if (!loaded.length && results.some((result) => result.status === "rejected")) {
        setError("Your notifications could not be loaded. Please try again.");
      }
      setLoading(false);
    });
  }, []);

  return (
    <main className="customer-account-page">
      <section className="customer-account-heading">
        <p className="eyebrow">Notifications</p>
        <h1>Table notifications</h1>
        <p>We’ll email you if a table opens for a time you requested.</p>
      </section>

      {loading && <p className="customer-account-loading">Loading notifications…</p>}
      {error && <p className="account-error" role="alert">{error}</p>}
      {!loading && !error && requests.length === 0 && (
        <div className="account-empty customer-account-empty">
          <span><BellRing /></span>
          <h2>No table notifications</h2>
          <p>When a sold-out time offers notifications, you can request one from the restaurant page.</p>
          <Button asChild><Link href="/search"><Search /> Find a table</Link></Button>
        </div>
      )}

      {requests.length > 0 && (
        <div className="customer-notification-list">
          {requests.map((request) => (
            <article key={request.publicId}>
              <header>
                <div><BellRing /><h2>Table notification</h2></div>
                <span data-status={request.status}>{statusLabel(request.status)}</span>
              </header>
              <div className="customer-notification-facts">
                <span><CalendarDays /> {request.requestedDates.map(formatDate).join(", ")}</span>
                <span><Clock3 /> {formatTime(request.timeFrom)}{request.timeUntil !== request.timeFrom ? `–${formatTime(request.timeUntil)}` : ""}</span>
                <span><Users /> {request.partySize} {request.partySize === 1 ? "guest" : "guests"}</span>
              </div>
              <small>Saved in this browser · Expires {formatDate(request.expiresAt)}</small>
            </article>
          ))}
        </div>
      )}
    </main>
  );
}

const statusLabel = (status: CustomerNotification["status"]) => ({
  active: "Watching",
  fulfilled: "Table found",
  cancelled: "Cancelled",
  expired: "Expired",
})[status];

const formatDate = (value: string) => {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const formatTime = (value: string) => {
  const [hour = "0", minute = "0"] = value.split(":");
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(2000, 0, 1, Number(hour), Number(minute)));
};
